import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useDviComponentTrends } from '@/hooks/useDviComponentTrends';
import { useComponentPromotionRow } from '@/hooks/useComponentPromotionState';
import { useAuth } from '@/contexts/AuthContext';
import type { StageReadinessRow } from '@/hooks/useDviStageReadiness';
import { pct, stageBadgeClass, stageLabel } from './stageColors';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

interface Props {
  row: StageReadinessRow | null;
  onClose: () => void;
}

export function ComponentConfidenceDetailDrawer({ row, onClose }: Props) {
  const open = !!row;
  const { activeShop } = useAuth();
  const { data: trends = [] } = useDviComponentTrends(row?.component ?? null, row?.condition_type ?? null, 30);
  const { data: promo } = useComponentPromotionRow(activeShop?.id ?? null, row?.component ?? null, row?.condition_type ?? null);

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        {row && (
          <>
            <SheetHeader>
              <SheetTitle>{row.component}</SheetTitle>
              <SheetDescription>{row.condition_type}</SheetDescription>
            </SheetHeader>

            <div className="mt-4 space-y-4">
              {/* Stages */}
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className={stageBadgeClass(row.component_stage)}>
                  Component: {stageLabel(row.component_stage)}
                </Badge>
                <Badge variant="outline" className={stageBadgeClass(row.severity_stage)}>
                  Severity: {stageLabel(row.severity_stage)}
                </Badge>
                <Badge variant="outline" className={stageBadgeClass(row.recommendation_stage)}>
                  Recommendation: {stageLabel(row.recommendation_stage)}
                </Badge>
              </div>

              {/* Evidence Health */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Evidence Health</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-muted-foreground">Total samples:</span> <span className="font-medium">{row.total_samples}</span></div>
                  <div><span className="text-muted-foreground">Distinct findings:</span> <span className="font-medium">{row.distinct_findings_count}</span></div>
                  <div><span className="text-muted-foreground">Multi-image findings:</span> <span className="font-medium">{row.multi_image_rollup_count}</span></div>
                  <div><span className="text-muted-foreground">Recent 30d:</span> <span className="font-medium">{row.recent_30d_samples}</span></div>
                </CardContent>
              </Card>

              {/* Trend chart */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Accuracy Trend (30d)</CardTitle>
                </CardHeader>
                <CardContent className="h-56">
                  {trends.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-xs text-muted-foreground">
                      No trend data yet — needs daily snapshots to accumulate.
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trends}>
                        <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                        <XAxis dataKey="bucket_date" tick={{ fontSize: 10 }} />
                        <YAxis domain={[0, 1]} tick={{ fontSize: 10 }} tickFormatter={(v) => `${Math.round(v * 100)}%`} />
                        <Tooltip formatter={(v: number) => `${(v * 100).toFixed(1)}%`} />
                        <Line type="monotone" dataKey="component_accuracy" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="severity_accuracy" stroke="hsl(var(--accent-foreground))" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="recommendation_accuracy" stroke="hsl(var(--muted-foreground))" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>

              {/* Acceptance / edits / rejections */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Decision Behavior</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-3 gap-3 text-sm">
                  <div><div className="text-muted-foreground text-xs">Acceptance</div><div className="font-medium">{pct(row.acceptance_rate)}</div></div>
                  <div><div className="text-muted-foreground text-xs">Edit rate</div><div className="font-medium">{pct(row.edit_rate)}</div></div>
                  <div><div className="text-muted-foreground text-xs">Rejection</div><div className="font-medium">{pct(row.rejection_rate)}</div></div>
                </CardContent>
              </Card>

              {/* Per-component OCR lift */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">OCR Effectiveness (this component)</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-3 gap-3 text-sm">
                  <div>
                    <div className="text-muted-foreground text-xs">Match w/ OCR</div>
                    <div className="font-medium">{pct(row.ocr_supported_component_agreement)}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-xs">Match w/o OCR</div>
                    <div className="font-medium">{pct(row.ocr_unsupported_component_agreement)}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-xs">Lift</div>
                    <div className="font-medium">
                      {row.ocr_lift_component_pct == null ? '—' : `${row.ocr_lift_component_pct.toFixed(1)}%`}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Stage readiness explanation */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Stage Readiness</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={row.automation_ready ? 'bg-primary/15 text-primary border-primary/30' : ''}>
                      {row.automation_ready ? 'Automation Ready' : 'Not Automation Ready'}
                    </Badge>
                  </div>
                  {row.readiness_blockers.length > 0 && (
                    <ul className="list-disc list-inside text-xs text-muted-foreground space-y-1">
                      {row.readiness_blockers.map((b) => <li key={b}>{b}</li>)}
                    </ul>
                  )}
                </CardContent>
              </Card>

              {/* Phase 15 — Why this decision */}
              {promo && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Why this decision</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">
                        {promo.recommended_action}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        Promo {promo.promotion_score.toFixed(2)} · Reg {promo.regression_score.toFixed(2)}
                      </span>
                    </div>
                    {promo.reason?.signals && (
                      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                        {Object.entries(promo.reason.signals).map(([k, v]: any) => (
                          <div key={k} className="flex justify-between">
                            <span className="text-muted-foreground">{k.replace(/_/g, ' ')}</span>
                            <span className="font-medium">{Number(v).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {promo.reason?.governance?.blockers?.length > 0 && (
                      <div className="text-xs text-muted-foreground">
                        Blockers: {promo.reason.governance.blockers.join(', ')}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
